import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Form, Alert, Spinner } from 'react-bootstrap';
import { FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';
import Header from '../../../header';
import Navvbar from '../../../Navvbar';
import './StatementEmail.css';
import { useAuth } from '../../../../login/AuthProvider';

const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

/* ============================================================
   AUTH HEADER HELPER
   All /api/pin/* endpoints require Authorization: Bearer <token>.
============================================================ */
const buildAuthHeaders = () => {
  const token =
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('idToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

/* Clear stale PIN session data */
const clearPinSession = () => {
  sessionStorage.removeItem('pinVerified');
  sessionStorage.removeItem('pinVerifiedForLab');
  sessionStorage.removeItem('pinVerifiedTimestamp');
};

const StatementEmail = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pinVerified, setPinVerified] = useState(false);
  const [hasExistingPassword, setHasExistingPassword] = useState(false);
  const [keepExistingPassword, setKeepExistingPassword] = useState(true);
  const [checkingVerification, setCheckingVerification] = useState(true);
  const navigate = useNavigate();
  const { labId } = useParams();

  // ⚠️ RACE CONDITION FIX: wait for AuthProvider to finish its /verify call
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Guard against setState on unmounted component
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  console.log('StatementEmail component - labId from URL:', labId);

  useEffect(() => {
    let cancelled = false;

    const checkPinVerification = async () => {
      if (!labId) {
        console.error('No labId provided in URL');
        if (cancelled || !isMountedRef.current) return;
        setError('Invalid URL. Please try again.');
        setTimeout(() => {
          if (!cancelled && isMountedRef.current) navigate('/Tabs');
        }, 2000);
        return;
      }

      try {
        const storedVerified = sessionStorage.getItem('pinVerified');
        const storedLabId = sessionStorage.getItem('pinVerifiedForLab');
        const storedTimestamp = sessionStorage.getItem('pinVerifiedTimestamp');

        const isValidSession =
          storedVerified === 'true' &&
          storedLabId === labId &&
          storedTimestamp &&
          Date.now() - parseInt(storedTimestamp, 10) < 30 * 60 * 1000;

        if (isValidSession) {
          console.log('Valid PIN verification found in sessionStorage');
          if (cancelled || !isMountedRef.current) return;
          setPinVerified(true);
          setCheckingVerification(false);
          fetchStatementEmail();
          return;
        }

        // Require a token before hitting a protected endpoint
        const headers = buildAuthHeaders();
        if (!headers.Authorization) {
          if (cancelled || !isMountedRef.current) return;
          clearPinSession();
          setError('Your session has expired. Please log in again.');
          setTimeout(() => {
            if (!cancelled && isMountedRef.current) navigate('/');
          }, 2000);
          return;
        }

        console.log('Checking PIN verification with backend for labId:', labId);
        const response = await axios.get(
          `${API_BASE_URL}/api/pin/lab/${labId}/check-verification`,
          { withCredentials: true, headers }
        );

        console.log('PIN verification response:', response.data);

        if (cancelled || !isMountedRef.current) return;

        if (response.data.verified) {
          sessionStorage.setItem('pinVerified', 'true');
          sessionStorage.setItem('pinVerifiedForLab', labId);
          sessionStorage.setItem('pinVerifiedTimestamp', Date.now().toString());
          setPinVerified(true);
          fetchStatementEmail();
        } else {
          clearPinSession();
          setError('PIN verification required. Redirecting to PIN setup...');
          setTimeout(() => {
            if (!cancelled && isMountedRef.current) {
              navigate(`/pin-setup/lab/${labId}`);
            }
          }, 2000);
        }
      } catch (err) {
        if (cancelled || !isMountedRef.current) return;
        console.error('Error checking verification:', err);
        const status = err.response?.status;
        const message = err.response?.data?.message || err.message || '';

        if (status === 401 || /access token required/i.test(message)) {
          clearPinSession();
          setError('Your session has expired. Please log in again.');
          setTimeout(() => {
            if (!cancelled && isMountedRef.current) navigate('/');
          }, 2000);
        } else {
          clearPinSession();
          setError('Session expired. Please verify PIN again.');
          setTimeout(() => {
            if (!cancelled && isMountedRef.current) {
              navigate(`/pin-setup/lab/${labId}`);
            }
          }, 2000);
        }
      } finally {
        if (!cancelled && isMountedRef.current) setCheckingVerification(false);
      }
    };

    // ⚠️ RACE CONDITION FIX — don't fire until AuthProvider has settled
    if (authLoading) {
      return () => {
        cancelled = true;
      };
    }

    if (!labId) {
      setCheckingVerification(false);
      return () => {
        cancelled = true;
      };
    }

    if (!isAuthenticated) {
      clearPinSession();
      setError('Your session has expired. Please log in again.');
      setTimeout(() => {
        if (!cancelled && isMountedRef.current) navigate('/');
      }, 1500);
      return () => {
        cancelled = true;
      };
    }

    checkPinVerification();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labId, navigate, authLoading, isAuthenticated]);

  const fetchStatementEmail = async () => {
    const headers = buildAuthHeaders();
    if (!headers.Authorization) {
      clearPinSession();
      setError('Your session has expired. Please log in again.');
      return;
    }

    try {
      console.log('Fetching statement email for labId:', labId);
      const response = await axios.get(
        `${API_BASE_URL}/api/pin/lab/${labId}/statement-email`,
        { withCredentials: true, headers }
      );
      console.log('Fetched statement email data:', response.data);
      if (!isMountedRef.current) return;
      setEmail(response.data.statementEmail || '');
      setHasExistingPassword(response.data.hasStatementPassword || false);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('Error fetching statement email:', err);
      const status = err.response?.status;
      const message = err.response?.data?.message || err.message || '';

      if (status === 401 || /access token required/i.test(message)) {
        clearPinSession();
        setError('Your session has expired. Please log in again.');
        setTimeout(() => {
          if (isMountedRef.current) navigate('/');
        }, 2000);
      } else if (status === 403) {
        clearPinSession();
        setError('PIN verification required. Redirecting...');
        setTimeout(() => {
          if (isMountedRef.current) navigate(`/pin-setup/lab/${labId}`);
        }, 2000);
      } else {
        setError(message || 'Error fetching email settings');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (email && !password && !hasExistingPassword) {
      setError('Password is required when setting a statement email');
      setLoading(false);
      return;
    }

    const payload = { statementEmail: email };

    if (email) {
      if (hasExistingPassword && keepExistingPassword) {
        console.log('Keeping existing password');
      } else if (password) {
        payload.statementPassword = password;
        console.log('Sending new password');
      }
    } else {
      console.log('Clearing email and password');
    }

    console.log('Sending payload:', {
      statementEmail: payload.statementEmail,
      hasPassword: payload.statementPassword ? 'Yes' : 'No',
    });

    const headers = buildAuthHeaders();
    if (!headers.Authorization) {
      setError('Your session has expired. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/pin/lab/${labId}/statement-email`,
        payload,
        { withCredentials: true, headers }
      );

      console.log('Update response:', response.data);
      if (!isMountedRef.current) return;
      setSuccess('Statement email updated successfully!');
      setPassword('');
      setKeepExistingPassword(true);
      setHasExistingPassword(response.data.hasPassword || false);

      setTimeout(() => {
        if (isMountedRef.current) setSuccess('');
      }, 3000);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('Error updating statement email:', err);
      const status = err.response?.status;
      const message = err.response?.data?.message || err.message || '';

      if (status === 401 || /access token required/i.test(message)) {
        clearPinSession();
        setError('Your session has expired. Please log in again.');
        setTimeout(() => {
          if (isMountedRef.current) navigate('/');
        }, 2000);
      } else if (status === 403) {
        clearPinSession();
        setError('PIN verification expired. Please verify again.');
        setTimeout(() => {
          if (isMountedRef.current) navigate('/Tabs');
        }, 2000);
      } else {
        setError(message || 'Error updating email');
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const handleClearEmail = () => {
    setEmail('');
    setPassword('');
    setHasExistingPassword(false);
    setKeepExistingPassword(true);
  };

  if (checkingVerification) {
    return (
      <>
        <Header />
        <Navvbar />
        <div className="se-wrapper">
          <div className="se-card se-card-center">
            <h3 className="se-title">Verifying PIN...</h3>
            <Spinner animation="border" variant="primary" />
            <p className="se-loading-text">
              Please wait while we verify your PIN...
            </p>
          </div>
        </div>
      </>
    );
  }

  if (!pinVerified) {
    return (
      <>
        <Header />
        <Navvbar />
        <div className="se-wrapper">
          <div className="se-card se-card-center">
            <h3 className="se-title-error">PIN Verification Required</h3>
            {error && <Alert variant="danger">{error}</Alert>}
            <button
              onClick={() => navigate(`/pin-setup/lab/${labId}`)}
              className="se-btn se-btn-primary"
              style={{ marginTop: '20px' }}
            >
              Go to PIN Setup
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <Navvbar />
      <div className="se-wrapper">
        <div className="se-card">
          <h2 className="se-title">Statement Email Settings</h2>

          {error && (
            <Alert variant="danger" style={{ marginBottom: '20px' }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" style={{ marginBottom: '20px' }}>
              {success}
            </Alert>
          )}

          {hasExistingPassword && email && (
            <div className="se-info-alert">
              <FaLock />
              You have an existing password set for this email.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <Form.Group className="se-form-group">
              <Form.Label className="se-label">
                Statement Email Address
              </Form.Label>
              <div className="se-email-row">
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g., statements@yourlab.com"
                  disabled={loading}
                  className="se-email-input"
                />
                {email && (
                  <button
                    type="button"
                    onClick={handleClearEmail}
                    className="se-btn se-btn-danger"
                  >
                    Clear
                  </button>
                )}
              </div>
              <Form.Text className="se-help-text">
                Leave empty to use default system email
              </Form.Text>
            </Form.Group>

            {email && (
              <>
                {hasExistingPassword && (
                  <Form.Group className="se-form-group-sm">
                    <div className="se-checkbox-group">
                      <Form.Check
                        type="checkbox"
                        id="keep-password"
                        checked={keepExistingPassword}
                        onChange={(e) => {
                          setKeepExistingPassword(e.target.checked);
                          if (e.target.checked) setPassword('');
                        }}
                      />
                      <label htmlFor="keep-password">Keep existing password</label>
                    </div>
                  </Form.Group>
                )}

                <Form.Group className="se-form-group">
                  <Form.Label className="se-label">
                    {hasExistingPassword && !keepExistingPassword
                      ? 'New Email Password'
                      : 'Email Password'}
                  </Form.Label>
                  <div className="se-password-wrapper">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={
                        hasExistingPassword
                          ? keepExistingPassword
                            ? 'Password is kept (unchanged)'
                            : 'Enter new password'
                          : 'Enter email password'
                      }
                      disabled={
                        loading || (hasExistingPassword && keepExistingPassword)
                      }
                      className={`se-password-input ${
                        hasExistingPassword && keepExistingPassword
                          ? 'se-password-input-disabled'
                          : ''
                      }`}
                    />
                    {!(hasExistingPassword && keepExistingPassword) && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="se-eye-btn"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    )}
                  </div>
                  <Form.Text className="se-help-text">
                    {hasExistingPassword && keepExistingPassword
                      ? 'Your existing password will be kept'
                      : 'This password will be stored securely'}
                  </Form.Text>
                </Form.Group>
              </>
            )}

            <div className="se-btn-row">
              <button
                type="button"
                onClick={() => navigate('/Tabs')}
                disabled={loading}
                className="se-btn se-btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="se-btn se-btn-primary"
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Saving...
                  </>
                ) : (
                  'Save Statement Email'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default StatementEmail;